import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatCurrency, publicPdfUrl } from "@/lib/api";
import { Download, Shield, Lock } from "lucide-react";

export default function PublicPayment() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api
      .get(`/public/invoices/${id}`)
      .then((r) => setInvoice(r.data))
      .catch(() => setInvoice(null))
      .finally(() => setLoading(false));
  }, [id]);

  const onPay = async () => {
    setPaying(true);
    // Simulate processing
    await new Promise((r) => setTimeout(r, 900));
    try {
      const { data } = await api.post(`/public/invoices/${id}/pay`);
      setInvoice(data);
      toast.success("Payment received");
      navigate(`/pay/${id}/success`);
    } catch {
      toast.error("Payment failed");
    } finally {
      setPaying(false);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-400 text-sm">
        Loading invoice…
      </div>
    );

  if (!invoice)
    return (
      <div className="min-h-screen flex items-center justify-center flex-col gap-3">
        <p className="text-zinc-500">This invoice does not exist.</p>
        <Link to="/" className="underline text-zinc-900">
          Home
        </Link>
      </div>
    );

  const isPaid = invoice.status === "paid";

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-white" data-testid="public-payment-page">
      {/* LEFT — Invoice details */}
      <div className="lg:col-span-3 p-8 sm:p-12 lg:p-16 flex flex-col">
        <div className="mb-12 flex items-center justify-between">
          <div className="font-display text-xl tracking-tight text-zinc-900">
            Invoice<span className="text-zinc-400">.</span>App
          </div>
          <a
            href={publicPdfUrl(invoice.id)}
            target="_blank"
            rel="noreferrer"
            data-testid="public-download-pdf"
            className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 transition-colors"
          >
            <Download size={14} /> Download PDF
          </a>
        </div>

        <div className="space-y-2 mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
            Invoice {invoice.invoice_number}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-zinc-900">
            {formatCurrency(invoice.total, invoice.currency)}
          </h1>
          <p className="text-zinc-500">
            Due from <span className="text-zinc-900">{invoice.client_name}</span>
          </p>
        </div>

        <div className="grid grid-cols-2 gap-8 pb-8 border-b border-zinc-200">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
              Billed to
            </p>
            <p className="text-zinc-900 mt-2">{invoice.client_name}</p>
            {invoice.client_email && (
              <p className="text-zinc-500 text-sm">{invoice.client_email}</p>
            )}
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
              Issued
            </p>
            <p className="text-zinc-900 mt-2">{invoice.created_at?.slice(0, 10)}</p>
            <p className="text-zinc-500 text-sm capitalize">Status: {invoice.status}</p>
          </div>
        </div>

        {/* Items */}
        <div className="mt-8 flex-1">
          <div className="grid grid-cols-12 gap-4 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 border-b border-zinc-900">
            <div className="col-span-7">Item</div>
            <div className="col-span-2 text-right">Qty</div>
            <div className="col-span-3 text-right">Amount</div>
          </div>
          {invoice.line_items.map((it, idx) => (
            <div
              key={idx}
              className="grid grid-cols-12 gap-4 py-5 border-b border-zinc-200 fade-up"
              style={{ animationDelay: `${idx * 70}ms` }}
              data-testid={`public-item-${idx}`}
            >
              <div className="col-span-7">
                <p className="text-zinc-900">{it.description}</p>
                <p className="text-zinc-500 text-sm">
                  {formatCurrency(it.rate, invoice.currency)} each
                </p>
              </div>
              <div className="col-span-2 text-right text-zinc-500 self-center">
                {Number(it.quantity)}
              </div>
              <div className="col-span-3 text-right text-zinc-900 font-medium self-center">
                {formatCurrency(it.quantity * it.rate, invoice.currency)}
              </div>
            </div>
          ))}

          <div className="flex justify-between items-baseline pt-6">
            <span className="font-display text-xl font-medium">Total due</span>
            <span
              className="font-display text-3xl font-medium"
              data-testid="public-total"
            >
              {formatCurrency(invoice.total, invoice.currency)}
            </span>
          </div>

          {invoice.notes && (
            <div className="mt-10 border-t border-zinc-200 pt-6">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                A note from the sender
              </p>
              <p className="text-zinc-700 mt-2 whitespace-pre-line">
                {invoice.notes}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* RIGHT — Pay panel */}
      <div className="lg:col-span-2 grain-bg p-8 sm:p-12 lg:p-16 flex flex-col justify-between border-l border-zinc-200">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
            Payment
          </p>
          <h2 className="font-display text-3xl font-medium tracking-tight text-zinc-900 mt-2">
            {isPaid ? "Settled. Thank you." : "Pay this invoice."}
          </h2>
          <p className="text-zinc-600 mt-3 leading-relaxed text-sm">
            {isPaid
              ? "This invoice has already been paid. No further action is needed."
              : "Tap the button below to settle. This is a mock checkout — no real card is charged."}
          </p>
        </div>

        <div className="my-10 space-y-3 py-8 border-y border-zinc-300">
          <div className="flex justify-between items-baseline">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
              Amount due
            </span>
            <span className="font-display text-4xl font-light text-zinc-900">
              {formatCurrency(isPaid ? 0 : invoice.total, invoice.currency)}
            </span>
          </div>
          <div className="flex justify-between text-sm text-zinc-500">
            <span>Reference</span>
            <span className="font-mono">{invoice.invoice_number}</span>
          </div>
        </div>

        <div className="space-y-4">
          {isPaid ? (
            <div
              data-testid="paid-confirmation"
              className="w-full text-center py-5 px-6 bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-bold uppercase tracking-[0.2em]"
            >
              Paid {invoice.paid_at?.slice(0, 10)}
            </div>
          ) : (
            <button
              onClick={onPay}
              disabled={paying}
              data-testid="pay-now-button"
              className="w-full bg-zinc-900 text-white rounded-none px-8 py-5 font-medium hover:bg-zinc-800 transition-colors disabled:opacity-60 text-lg"
            >
              {paying ? "Processing…" : `Pay ${formatCurrency(invoice.total, invoice.currency)} →`}
            </button>
          )}

          <div className="flex items-center justify-center gap-5 text-xs text-zinc-500 pt-2">
            <span className="inline-flex items-center gap-1.5">
              <Lock size={12} /> Secure
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Shield size={12} /> Mock checkout
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
